import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { QrCode } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../lib/api";

export default function Login() {
  const { login, verify2fa, expired } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/app";

  const [form, setForm] = useState({ email: "", password: "" });
  const [awaiting2fa, setAwaiting2fa] = useState(false);
  const [code2fa, setCode2fa] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (awaiting2fa) {
        await verify2fa(code2fa);
      } else {
        const res = await login(form.email, form.password);
        if (res?.requires_2fa) {
          setAwaiting2fa(true);
          setLoading(false);
          return;
        }
      }
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-12 px-4 rounded-xl border border-zinc-200 bg-white text-base placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow";

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex flex-col items-center mb-8">
          <span className="h-14 w-14 rounded-2xl bg-zinc-900 text-white flex items-center justify-center mb-4">
            <QrCode size={26} />
          </span>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Asset Passport</h1>
          <p className="text-zinc-500 text-sm mt-1.5 text-center">
            Toda la información de tus máquinas, en un único lugar.
          </p>
        </div>

        {expired && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4" data-testid="session-expired-message">
            Tu sesión ha caducado. Inicia sesión de nuevo.
          </p>
        )}

        <div className="bg-white rounded-3xl border border-zinc-200/80 card-shadow p-7 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            {awaiting2fa ? (
              <>
                <p className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3">
                  Tu cuenta tiene verificación en dos pasos. Introduce el código de 6 dígitos de tu
                  aplicación de autenticación.
                </p>
                <input
                  className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono`}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="······"
                  value={code2fa}
                  onChange={(e) => setCode2fa(e.target.value.replace(/\D/g, ""))}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  data-testid="login-2fa-code-input"
                />
              </>
            ) : (
              <>
                <input
                  className={inputCls}
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={set("email")}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                />
                <input
                  className={inputCls}
                  type="password"
                  placeholder="Contraseña"
                  value={form.password}
                  onChange={set("password")}
                  required
                  minLength={6}
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <div className="text-right -mt-1">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </>
            )}

            {error && (
              <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-2.5" data-testid="auth-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
            >
              {loading ? "Un momento…" : awaiting2fa ? "Verificar código" : "Entrar"}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-zinc-400 mt-6">
          ¿Aún no tienes acceso?{" "}
          <Link
            to="/solicitar-acceso"
            className="underline underline-offset-2 hover:text-zinc-600 transition-colors"
            data-testid="login-request-access-link"
          >
            Solicita acceso al programa piloto
          </Link>
        </p>
      </div>
    </div>
  );
}
